<script>
  // One occurrence on the quest edit page's Generated Quests tab. Renders and
  // reports intent only; the host page owns the modals and requests.
  import dayjs from 'dayjs'
  import ClickableImage from '../lightbox/clickable_image.svelte'
  import ClickableVideo from '../lightbox/clickable_video.svelte'
  import { Badge, Button } from '../ui'
  import { badgeFor } from '../../lib/ui/badges.js'

  let {
    quest,
    highlighted = false,
    onratedposts,
    onpickexample,
    oncreateexample,
    onunmark,
    onthumbnail,
    oncopylink
  } = $props()

  const THUMB_CLASS = 'w-20 h-20 object-contain bg-base-200 rounded-box'

  const formatAt = (at) => dayjs(at).format('MMM D, h:mma')
  const formatWindow = (start, end) => {
    if (!start && !end) return '—'
    return `${start ? formatAt(start) : '?'} → ${end ? formatAt(end) : '?'}`
  }

  const status = $derived(badgeFor('quest_lifecycle', quest.lifecycle_status))
  // A "next" occurrence the app already lists as Coming Soon.
  const visibleAhead = $derived(quest.lifecycle_status === 'coming_soon' && !dayjs().isBefore(quest.visible_starts_at))
  const example = $derived(quest.example_submission)
  const exampleMoment = $derived(example?.moment)
</script>

<div class="card bg-base-100 mb-3 {highlighted ? 'border-2 border-primary' : 'card-border'}">
  <div class="card-body p-4 gap-1">
    <div class="flex flex-wrap justify-between items-start gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <strong>{quest.cycle_identifier}</strong>
        <span class="badge badge-sm {status.badgeClass}">{status.label}</span>
        {#if visibleAhead}
          <span class="text-xs text-base-content/60" title="Already listed in the app's quest carousel as Coming Soon">Visible in app now</span>
        {/if}
      </div>
      <div class="flex flex-wrap items-center gap-2">
        {#if quest.submissions_count > 0}
          <Badge
            variant="primary"
            soft
            size="sm"
            title="Schedule changes that would remove this occurrence leave it live instead, because people have submitted to it."
          >
            {quest.submissions_count} submission{quest.submissions_count === 1 ? '' : 's'}
          </Badge>
        {/if}
        <Button
          variant="ghost"
          size="xs"
          icon="star"
          title="Rated posts (Good / Great / Best) — shown up front in Quest Detail"
          onclick={() => onratedposts?.(quest)}
        >
          Rated ({quest.rated_submissions_count ?? 0})
        </Button>
        {#if quest.share_url}
          <Button
            variant="ghost"
            size="xs"
            icon="link"
            title="Copy this Quest's share link (just the URL, credited to no one) for Intercom and OneSignal deep links"
            onclick={() => oncopylink?.(quest.share_url)}
          >
            Copy link
          </Button>
        {/if}
        <span class="text-xs text-base-content/50">id: {quest.id}</span>
      </div>
    </div>
    <div class="text-sm"><strong>Visible:</strong> {formatWindow(quest.visible_starts_at, quest.visible_ends_at)}</div>
    <div class="text-sm"><strong>Active:</strong> {formatWindow(quest.active_starts_at, quest.active_ends_at)}</div>
    <p class="text-sm text-base-content/70">{quest.prompt}</p>

    {#if example}
      <div class="flex flex-wrap items-center gap-3 mt-2">
        {#if exampleMoment?.video_url}
          <ClickableVideo url={exampleMoment.video_url} thumbnailUrl={exampleMoment.thumbnail_url} thumbClass={THUMB_CLASS} />
        {:else if exampleMoment?.thumbnail_url}
          <ClickableImage url={exampleMoment.thumbnail_url} title="Example submission" thumbClass={THUMB_CLASS} />
        {/if}
        <div class="grow text-sm">
          <Badge variant="success" size="sm">Example</Badge>
          {#if example.user?.name}<span class="ml-2">{example.user.name}</span>{/if}
        </div>
        {#if exampleMoment?.video_url && onthumbnail}
          <Button
            variant="default"
            outline
            size="sm"
            icon="thumbnail"
            title="Pick a different poster frame for this video"
            onclick={() => onthumbnail?.({ momentId: exampleMoment.id, videoUrl: exampleMoment.video_url, thumbnailUrl: exampleMoment.thumbnail_url })}
          >
            Thumbnail
          </Button>
        {/if}
        <Button variant="error" outline size="sm" onclick={() => onunmark?.(example.id)}>Remove Example</Button>
      </div>
    {:else}
      <div class="flex gap-2 mt-2">
        <Button variant="primary" outline size="sm" onclick={() => onpickexample?.(quest)}>Mark Example</Button>
        {#if quest.can_create_example}
          <Button variant="default" outline size="sm" icon="add" onclick={() => oncreateexample?.(quest)}>Create Example</Button>
        {/if}
      </div>
    {/if}
  </div>
</div>
